import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Copy, Check } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";

export default function Security2FA() {
  const [step, setStep] = useState("start"); // start | enrolling | backupCodes
  const [enrollData, setEnrollData] = useState(null); // { secret, otpauth_uri }
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  const startEnroll = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/2fa/enroll");
      setEnrollData(res.data);
      setStep("enrolling");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start 2FA enrollment.");
    } finally {
      setLoading(false);
    }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/2fa/verify-enroll", { code: code.trim() });
      setBackupCodes(res.data.backup_codes);
      setStep("backupCodes");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code. Check your authenticator app and try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopied(true);
    toast.success("Backup codes copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const qrImageUrl = enrollData
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(enrollData.otpauth_uri)}`
    : null;

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Two-Factor Authentication</h1>
          <p className="text-slate-500 text-sm">Add an extra layer of security to your admin account.</p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          {step === "start" && (
            <div className="text-center py-6">
              <ShieldCheck className="w-12 h-12 text-brand-500 mx-auto mb-4" />
              <h2 className="font-heading font-bold text-lg uppercase mb-2">Enable 2FA</h2>
              <p className="text-slate-500 text-sm mb-6">
                You'll need an authenticator app like Google Authenticator, Zoho OneAuth, or Authy on your phone.
              </p>
              <button onClick={startEnroll} disabled={loading} data-testid="start-2fa-enroll"
                className="px-6 py-3 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70 uppercase">
                {loading ? "Starting..." : "Get Started"}
              </button>
            </div>
          )}

          {step === "enrolling" && enrollData && (
            <div>
              <h2 className="font-heading font-bold text-lg uppercase mb-4">Scan This QR Code</h2>
              <div className="flex justify-center mb-4">
                <img src={qrImageUrl} alt="2FA QR Code" className="border border-slate-200 rounded-sm" data-testid="2fa-qr-code" />
              </div>
              <p className="text-xs text-slate-500 mb-2">Can't scan? Enter this code manually in your app:</p>
              <div className="bg-slate-50 rounded-sm p-3 border border-slate-200 mb-6 text-center">
                <p className="text-sm font-mono font-semibold text-slate-800 break-all" data-testid="2fa-secret">{enrollData.secret}</p>
              </div>

              <form onSubmit={confirmEnroll} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Enter the 6-digit code from your app</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="000000"
                    required
                    autoFocus
                    data-testid="2fa-verify-code"
                    className="w-full px-4 py-3 border border-slate-200 rounded-sm text-sm text-center tracking-widest font-mono focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-slate-50 focus:bg-white"
                  />
                </div>
                <button type="submit" disabled={loading} data-testid="2fa-confirm-submit"
                  className="w-full py-3 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70 uppercase">
                  {loading ? "Verifying..." : "Confirm & Enable 2FA"}
                </button>
              </form>
            </div>
          )}

          {step === "backupCodes" && backupCodes && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-emerald-500" />
                <h2 className="font-heading font-bold text-lg uppercase text-emerald-600">2FA Enabled</h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Save these backup codes somewhere safe. Each can be used once if you lose access to your authenticator app. They will not be shown again.
              </p>
              <div className="bg-slate-50 rounded-sm p-4 border border-slate-200 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((c) => (
                    <p key={c} className="text-sm font-mono font-semibold text-slate-800">{c}</p>
                  ))}
                </div>
              </div>
              <button onClick={copyBackupCodes} data-testid="copy-backup-codes"
                className="w-full py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm hover:bg-slate-50 flex items-center justify-center gap-2 mb-3">
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied!" : "Copy All Codes"}
              </button>
              <p className="text-center text-xs text-slate-400">You're all set. 2FA is now required on every login.</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
